import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import LangToggle from './LangToggle';
import { useState, useCallback } from 'react';
import useAuthStore from '../stores/authStore';
import api from '../services/api';
import useLang from '../hooks/useLang';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, login } = useAuthStore();
  const { T } = useLang();
  const roleLabel = { superadmin: T.role.superadmin, admin: T.role.admin, member: T.role.member };

  // Group switcher state
  const [switchData, setSwitchData] = useState(null);   // { select_token, groups }
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');

  const openSwitcher = useCallback(async () => {
    setSwitchLoading(true);
    setSwitchError('');
    try {
      const { data } = await api.post('/auth/switch-group');
      setSwitchData(data);
    } catch (err) {
      setSwitchError(err.response?.data?.error || T.layout.errFetchGroups);
    } finally {
      setSwitchLoading(false);
    }
  }, [T]);

  const chooseGroup = useCallback(async (memberId, effectiveRole) => {
    setSwitching(true);
    setSwitchError('');
    try {
      const { data } = await api.post('/auth/choose-group', {
        select_token:  switchData.select_token,
        member_id:     memberId,
        effective_role: effectiveRole,
      });
      login(data.token, data.user);
      setSwitchData(null);
      // Reload page to refresh all data for new group
      window.location.href = '/';
    } catch (err) {
      setSwitchError(err.response?.data?.error || T.layout.errSwitchGroup);
      setSwitching(false);
    }
  }, [switchData, login, T]);

  const currentGroupId = user?.group_id;
  const currentRole    = user?.role;

  // Map effective_role to display label
  const effectiveRoleLabel = (role) => {
    if (role === 'admin' || role === 'mwenyekiti') return { label: T.role.admin,  cls: 'bg-emerald-100 text-emerald-700' };
    return                                                 { label: T.role.member, cls: 'bg-slate-100 text-slate-600'    };
  };

  return (
    <div className="app-shell min-h-screen">
      <div className="app-shell__aurora app-shell__aurora--teal" />
      <div className="app-shell__aurora app-shell__aurora--amber" />
      <div className="app-shell__aurora app-shell__aurora--navy" />

      {/* Group switcher modal */}
      {switchData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="font-bold text-slate-800">{T.layout.switchGroup}</p>
                <p className="text-xs text-slate-400">{T.layout.chooseGroup}</p>
              </div>
              <button
                onClick={() => { setSwitchData(null); setSwitchError(''); }}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {switchError && (
              <p className="mx-5 mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{switchError}</p>
            )}

            <div className="space-y-2 p-4">
              {switchData.groups.map(g => {
                const isCurrent = String(g.member_id) === String(user?.member_id) && g.effective_role === user?.role;
                const roleInfo  = effectiveRoleLabel(g.effective_role ?? g.role);
                return (
                  <button
                    key={g.group_id}
                    onClick={() => !isCurrent && chooseGroup(g.member_id, g.effective_role)}
                    disabled={switching || isCurrent}
                    className={`flex w-full items-center justify-between rounded-xl border-2 p-4 text-left transition-all disabled:cursor-not-allowed ${
                      isCurrent
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-slate-100 bg-slate-50 hover:border-emerald-300 hover:bg-emerald-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{g.name}</p>
                      {g.code && <p className="text-xs text-slate-400">#{g.code}</p>}
                      {/* Role badge for this group */}
                      <span className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${roleInfo.cls}`}>
                        {roleInfo.label}
                      </span>
                    </div>
                    <div className="ml-3 flex shrink-0 flex-col items-end gap-1">
                      {isCurrent ? (
                        <span className="rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white">
                          {T.layout.currentGroup}
                        </span>
                      ) : switching ? (
                        <svg className="h-4 w-4 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                      ) : (
                        <span className="text-slate-400 text-lg">›</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="relative flex min-h-screen">
        <Sidebar
          open={sidebarOpen}
          onOpen={() => setSidebarOpen(true)}
          onClose={() => setSidebarOpen(false)}
        />

        <div className="flex min-h-screen min-w-0 flex-1 flex-col xl:pl-[19rem]">
          <header className="sticky top-0 z-20 px-3 pt-3 sm:px-4 sm:pt-4 lg:px-6">
            <div className="mx-auto flex max-w-7xl items-center gap-3 rounded-[28px] border border-white/65 bg-white/72 px-4 py-3 shadow-[0_20px_50px_rgba(15,23,42,0.10)] backdrop-blur-xl sm:px-5 lg:px-6">
              <button
                className="xl:hidden flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.25)]"
                onClick={() => setSidebarOpen(true)}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h10" />
                </svg>
              </button>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-teal-700/80">{T.layout.appLabel}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="truncate text-lg font-semibold text-slate-900 sm:text-xl">{T.layout.welcome}, {user?.name || T.common.user}</span>
                </div>
              </div>

              {/* Language toggle */}
              <LangToggle className="shrink-0" />

              {/* Group switcher button — members and admins (not superadmin) */}
              {(user?.role === 'member' || user?.role === 'admin') && (
                <button
                  onClick={openSwitcher}
                  disabled={switchLoading}
                  title={T.layout.switchGroupTitle}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-green-400 hover:bg-green-50 hover:text-green-700 disabled:opacity-50"
                >
                  {switchLoading ? (
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                    </svg>
                  )}
                </button>
              )}

              <div className="hidden rounded-2xl bg-[linear-gradient(135deg,rgba(20,184,166,0.12),rgba(249,115,22,0.12))] px-4 py-2 text-right sm:block">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{T.layout.roleLabel}</p>
                <p className="text-sm font-semibold text-slate-800">{roleLabel[user?.role] || T.common.user}</p>
              </div>
            </div>
          </header>

          <main className="flex-1 px-3 pb-[7.5rem] pt-4 sm:px-4 lg:px-6 lg:pb-8">
            <div className="mx-auto max-w-7xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
